import React from 'react';
import produce from 'immer';

import { Dataset, Device, ParsedMessage, IBACReading, GPSReading, METReading, AlgorithmResult, PlottableSensorReading, PlottableSensorTypes } from './BD21Device'
import { BD21DataStream, DataPacket, GroupedLocationMessage, StreamType, StreamStatus, DoseMapMessage, DoseDatatype } from './BD21DataStream';
import { AuthContext } from './BD21Auth'
import { BD21SimThriftDataStream } from './BD21SimThriftDataStream';

import { FakeDataGenerator } from './utils/FakeData';
import { uuidToString, generateRandomId } from './utils/UUID';
import * as invStorage from './utils/InvestigationStorage';
import * as uiStorage from './utils/UISettingStorage';

import { BD21StatusHeader } from './components/BD21StatusHeader'
//import { BioInvestigation, BioInvestigationDisplay } from './components/BioInvestigationDisplay'
import { BioInvestigationEncounterDisplay, InvestigationInterface, InvTypeToShow} from './components/BioInvestigationEncounterDisplay'
import { MultiSensorMapDisplay, SensorPosition} from './components/MultiSensorMapDisplay'
import { IndoorMapDisplay } from './components/IndoorMapDisplay'
import { BioCountDisplayButton, MultiSensorBioCountDisplay, SensorDisplaySelection ,PlotSelectInterface} from './components/BioCountPlotDisplay'

import NodeHealth, { ConvertSensorNameToMakeModel, GetCloudUIUrl, HasCloudUI, NodeHealthData, SensorHealthData } from './NodeHealth';
import * as messages from '@radnuc/messages';
import * as thrift from 'thrift';

import {DoseAnalysisType, DoseMapUtility} from './utils/DoseMapUtility'

import { Config } from '@radnuc/react-ui-components';

import './App.css';

import previousFrame from '@radnuc/react-ui-components/dist/img/previous-frame-32-white.png';
import advanceFrame from '@radnuc/react-ui-components/dist/img/advance-frame-32-white.png';
import { Core } from '@radnuc/messages';
import { ModalityFilterSetting, ModalityFilterInterface, ModalitySelectEnum, getInvToShowFromModalityFilterSetting } from './components/ModalityFilterDisplay';
import { DoseMapAnalysisDisplay, DoseMapAnalysisInterface, DoseMapLiveString, DoseMapLoadFromFileString, DoseMapTypesEnum, BusyStatus, ScaleOverride, DoseMapMinMaxEnum, DoseMapLoadedFromFileString, DoseMapStats } from './components/DoseMapDisplay';
import { CloudUiDisplay, CloudUiDisplayInterface, CloudUiDisplayParam} from './components/CloudUiDisplay';
import { SuppressedAlertsInterface } from './components/BD21ExpertSettingsDisplay';


type Investigation = messages.Encounter.Encounter | messages.Rad.Investigation; // BioInvestigation;
enum UIConnectivityStatus {CONNECTED,ATTENTION,DISCONNECTED};
const API_ROOT = window.app && window.app.env.API_URL;

export interface AppState {
    connected: boolean,
    UIConnectivityStatus: UIConnectivityStatus,
    streamStatus: StreamStatus,

    selectedDisplay1: string,
    selectedDisplay2: string,

    //device: Device,
    devices: { [key: string]: Device },

	selectedDeviceSensorRealtime: {device:string,sensor:string,follow:boolean} | undefined,
    selectedDeviceSensorReplay: {device:string,sensor:string,follow:boolean} | undefined,

    selectedDevicesRealtime: {[key:string]: SensorDisplaySelection},
    selectedDevicesReplay: {[key:string]: SensorDisplaySelection},

    mapPlotVisibility: boolean,

    health: NodeHealth,
    config: Config,

    doseMaps: {[key:string]:DoseMapMessage | undefined},//DoseMapMessage | undefined,
    doseMapSelected: string | undefined,
    doseMapType: DoseMapTypesEnum|undefined,
    doseMapLive: boolean,
    doseMapAnalysisType: DoseAnalysisType|undefined;
    doseMapControlOpen: boolean,
    doseMapBusy: {[key:string]:BusyStatus},
    doseMapScaleOverride: {[key:number]:{[key:string]:ScaleOverride}},
    doseMapDateAvailable: string[],
    doseMapDateSelected: {[key:string]:string},
    doseMapHideAlertPins?: boolean,

    cloudUiDisplayParam?: CloudUiDisplayParam,

    trackedDevice?: string,

    centerOnAlert: boolean,
    investigations: { [key: string]: Investigation },

    selectedInvestigation?: string,
    replayInvestigation?: string,
    replayTime?: number,
    investigationData?: { [key: string]: Device },

    invTypeToShow?: InvTypeToShow|null,

    timeOffset: number,

    investigationsCollapsed: { [key: string]: Investigation },

    modalityFilterSetting: ModalityFilterSetting
    showSuppressedAlerts: boolean,
}

export class App extends React.Component<{}, AppState> {
    readonly investigationPadding = 60000;
    //readonly connect_via_ws = true;
    readonly loadPastEncounters = 24 * 7; //hacked to get network alert on 5/8  //24; //hrs

    readonly closeUnfinalizedInv = 60000; //Seconds

    readonly getTier0Tier2Results = true; //Receive Tier0 and Tier2 from AWS
    readonly hideTier0Encounters = false; //Scrub Tier0 results from Encounter
    //readonly uiVersionString = this.getTier0Tier2Results? (this.hideTier0Encounters?"2023/2r5.T0FT2":"2023/2r5.T0T2") : (this.hideTier0Encounters?"2023/2r5.T0F":"2023/2r5");

    readonly uiVersionString = "2026/9r1"


    //ws: WebSocket | null = null;

    /*
    stream: BD21SimThriftDataStream = new BD21SimThriftDataStream({
        onConnect: this.handleOpen.bind(this),
        onDisconnect: this.handleClose.bind(this),
        onMessage: this.handleMessage.bind(this),
        onHealthMessage: this.handleHealthMessage.bind(this)
    });
    */


    context!: React.ContextType<typeof AuthContext>

    dataStream: BD21DataStream = new BD21DataStream(StreamType.DATA,{
        onConnect: this.handleOpen.bind(this),
        onDisconnect: this.handleClose.bind(this),
        onMessage: this.handleMessage.bind(this),
        onBulkMessage: this.handleBulkMessage.bind(this),
    });

    healthStream: BD21DataStream = new BD21DataStream(StreamType.HEALTH,{
            onConnect: this.handleOpen.bind(this),
            onDisconnect: this.handleClose.bind(this),
            onHealthMessage: this.handleHealthMessage.bind(this),
            //onLocationMessage: this.handleLocationMessage.bind(this)
    });

    encounterStream: BD21DataStream = new BD21DataStream(StreamType.ENCOUNTER,{
            onConnect: this.handleOpen.bind(this),
            onDisconnect: this.handleClose.bind(this),
            onMessage: this.handleMessage.bind(this),
    });

    locationStream: BD21DataStream = new BD21DataStream(StreamType.LOCATION,{
            onConnect: this.handleOpen.bind(this),
            onDisconnect: this.handleClose.bind(this),
            onLocationMessage: this.handleLocationMessage.bind(this)
    });

    doseMapStream: BD21DataStream = new BD21DataStream(StreamType.DOSE_MAP,{
            onConnect: this.handleOpen.bind(this),
            onDisconnect: this.handleClose.bind(this),
            onDoseMapMessage: this.handleDoseMapMessage.bind(this),
            onDoseMapLoadProgress: this.handleDoseMapLoadProgress.bind(this)
    });



    refreshTimer: NodeJS.Timeout | null = null;
    useTTSVoice = 'Zira';

    //encounterRefreshTimer: NodeJS.Timeout | null = null;

    shutdown: boolean = false;
    displayModes: string[] = ['Map', 'Count History'];//, 'Indoor Map'];

    // Options for testing
    fakeData: FakeDataGenerator;
    forceInvestigationPosition: [number, number] = [0, 0]

    investigationStatus: { [key: string]: { lastTimestamp: number, investigationId: string } } = {};
    investigationCloseTimeSec: number = 10; //Close out investigation [sec]
    filterInvestigationsByNodeHealth = false;

    encounterHistoryQueried:boolean = false;
    doseMapSummaryQueried:boolean = false;

    constructor(props: {}) {
        super(props);
        this.state = {
            connected: false,
            UIConnectivityStatus: UIConnectivityStatus.DISCONNECTED,
            streamStatus: {data:false,health:false,encounter:false,location:false,doseMap:false},
            devices: {},
			selectedDeviceSensorRealtime: undefined,
            selectedDeviceSensorReplay: undefined,
            selectedDevicesRealtime: {},
            selectedDevicesReplay: {},

            mapPlotVisibility: false,

            selectedDisplay1: 'Map',
            selectedDisplay2: 'Context',

            //device: new Device('SNTEMP'),

            health: new NodeHealth(),
            config: new Config(),
            doseMaps: {["A"]:undefined,["B"]:undefined,["C"]:undefined}, //undefined
            doseMapSelected: "A",
            doseMapType: undefined,
            doseMapLive: false,
            doseMapAnalysisType: undefined,
            doseMapBusy: {["A"]:{flag:false,percent:-1},["B"]:{flag:false,percent:-1},["C"]:{flag:false,percent:-1},["MAP"]:{flag:false,percent:-1}},
            doseMapScaleOverride: {[DoseMapTypesEnum.DOSEMAP]:{["A"]:{min:false,max:false},["B"]:{min:false,max:false},["C"]:{min:false,max:false},['GANG']:{min:false,max:false}},
                                   [DoseMapTypesEnum.DOSEMAX]:{["A"]:{min:false,max:false},["B"]:{min:false,max:false},["C"]:{min:false,max:false},['GANG']:{min:false,max:false}},
                                   [DoseMapTypesEnum.UNCERTAINTY]:{["A"]:{min:false,max:false},["B"]:{min:false,max:false},["C"]:{min:false,max:false},['GANG']:{min:false,max:false}}},
            doseMapDateAvailable: [],
            doseMapDateSelected:{["A"]:"",["B"]:"",["C"]:""},
            doseMapControlOpen: false,
            doseMapHideAlertPins: false,

            cloudUiDisplayParam: undefined,
            trackedDevice: undefined,

            centerOnAlert: false,
            investigations: {},
            investigationsCollapsed: {},
            timeOffset: 0,

            invTypeToShow: this.getTier0Tier2Results? [messages.Encounter.EncounterType.BIOSIGMA,messages.Encounter.EncounterType.CHEMSIGMA,messages.Encounter.EncounterType.RN_FUSION] as InvTypeToShow : null,    //SHow all inv type by default
            modalityFilterSetting: {rad: ModalitySelectEnum.NONE, chem: ModalitySelectEnum.NONE, bio: ModalitySelectEnum.NONE},

            showSuppressedAlerts: false,
        };

        this.fakeData = new FakeDataGenerator(this.handleMessage.bind(this), { cbsigma: false, bio: false, met: false, radEncounter: false});
        MultiSensorMapDisplay.clearMapInfoSessionStorage(); //Clear map info session storage

        if(this.getTier0Tier2Results){
            this.encounterStream.setTier0_2ResultFlag(true);
        }
    }

    handleRefresh() {
        if (this.context) {
            console.log('refresh')
            this.context.refresh(() => {
                if (this.context && this.context.jwtToken) {
                    console.log('token', this.context.jwtToken)
                    this.dataStream.setToken(this.context.jwtToken)
                    this.healthStream.setToken(this.context.jwtToken)
                    this.encounterStream.setToken(this.context.jwtToken)
                    this.locationStream.setToken(this.context.jwtToken)
                    if(API_ROOT.Modalities.RadDoseMap)
                        this.doseMapStream.setToken(this.context.jwtToken)
                }
            })
        }

        this.refreshTimer = setTimeout(() => {
            this.handleRefresh()
        }, 300000)
    }

    setStreamStatus(streamType:StreamType,flag:boolean){
        this.setState(produce(draft => {
            if (streamType === StreamType.DATA){
                draft.streamStatus.data = flag;
            } else if (streamType === StreamType.HEALTH){
                draft.streamStatus.health = flag;
            } else if (streamType === StreamType.ENCOUNTER){
                draft.streamStatus.encounter = flag;
            } else if (streamType === StreamType.LOCATION){
                draft.streamStatus.location = flag;
            } else if (streamType === StreamType.DOSE_MAP){
                draft.streamStatus.doseMap = flag;
            }
        }));
    }
    setStreamStatusSummary() {
        if(this.state.streamStatus.data && this.state.streamStatus.health && this.state.streamStatus.encounter && this.state.streamStatus.location){
            this.setState({UIConnectivityStatus: UIConnectivityStatus.CONNECTED});
        } else if(this.state.streamStatus.data || this.state.streamStatus.health || this.state.streamStatus.encounter || this.state.streamStatus.location){
            this.setState({UIConnectivityStatus: UIConnectivityStatus.ATTENTION});
        } else {
            this.setState({UIConnectivityStatus: UIConnectivityStatus.DISCONNECTED});
        }
    }
    handleOpen(streamType:StreamType) {
        //console.log('connected');
        this.setStreamStatus(streamType,true);
        this.setStreamStatusSummary();
        this.setState({connected: true });
        //this.setState({ health: this.state.health.reconnect() }); //Reset Health state
        this.refreshTimer = setTimeout(() => {
            this.handleRefresh()
        }, 300000)

        /*
        this.encounterRefreshTimer = setTimeout(() => {
            this.handleCheckEncounterForUnfinalized()
        }, 60000)
        */

    }

    handleClose(streamType:StreamType) {
        //console.log('disconnected');
        this.setStreamStatus(streamType,false);
        this.setState({ connected: false });

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        // Restart WebSocket unless we are unmounting
        if (!this.shutdown) {
            setTimeout(() => {
                if (this.context?.jwtToken){
                    console.log("Reconnecting", streamType);
                    if (streamType === StreamType.DATA){
                        this.dataStream.connect(this.context?.jwtToken);
                    } else if (streamType === StreamType.HEALTH){
                        this.healthStream.connect(this.context?.jwtToken);
                    } else if (streamType === StreamType.ENCOUNTER){
                        this.encounterStream.connect(this.context?.jwtToken);
                    } else if (streamType === StreamType.LOCATION){
                        this.locationStream.connect(this.context?.jwtToken);
                    } else if (streamType == StreamType.DOSE_MAP){
                        if(API_ROOT.Modalities.RadDoseMap)
                            this.doseMapStream.connect(this.context.jwtToken);
                    }
                }
            }, 5000)
        }
    }
    handleBulkMessage(data: DataPacket[]) {
        //N.B. Bulk messages are of same device and sensor
        let messages:PlottableSensorReading[] = [];
        data.forEach((packet) => {
            let [header, this_message] = packet;
            if (this_message !== null && 'device' in this_message){
                if (this_message instanceof PlottableSensorReading) {
                    messages.push(this_message);
                }
            }
        });
        if(messages.length > 0){
            const deviceName = messages[0].device;
            this.setState(produce(draft => {
                if (this.state.devices[deviceName] === undefined) {
                    draft.devices[deviceName] = new Device(deviceName);
                }
                draft.devices[deviceName] = draft.devices[deviceName].bulkUpdate(messages);
            }));
        }
    }


    handleMessage(data: DataPacket[]) {
        //console.log('Message received')
        data.forEach((packet) => {
            let [header, message] = packet;

            //Header contains status message
            if('statusString' in header){
                const deviceName = header.device_name;
                this.setState(produce(draft => {
                    if (this.state.devices[deviceName] === undefined) {
                        draft.devices[deviceName] = new Device(deviceName);
                    }
                    draft.devices[deviceName] = draft.devices[deviceName].updateStatus(header.statusString);
                }));
            }
            if(message !== null){
                if(messages.Encounter.Encounter.prototype.isPrototypeOf(message)){
                    //console.log('is Encounter!')
                    this.setState(produce(draft => {
                        let inv = message as messages.Encounter.Encounter;
                        inv.node_name = header.device_name;

                        console.log('Encouter msg: '+uuidToString(inv.id)+' node_name:'+inv.node_name+' type:'+inv.type+' finalized:'+inv.finalized+' starttime:'+inv.start_time+' endtime:'+inv.end_time)
                        //Hide any suppressed alerts
                        if(this.state.showSuppressedAlerts || inv.suppressed !== true){
                            //Filter any inv that are not in the health node list
                            if(this.isInvestigationInNodeList(inv)){
                                if(this.hideTier0Encounters){
                                    //if(!this.isTier0OnlyEncounter(inv)){
                                    //    this.filterAlarmingSensors(inv);
                                    //    draft.investigations[uuidToString(inv.id)] = inv;
                                    //}
                                    if(!this.isSensorLevelEncounter(inv)){
                                        //this.filterAlarmingSensors(inv);
                                        if(draft.investigations[uuidToString(inv.id)] === undefined){
                                            draft.investigations[uuidToString(inv.id)] = inv;
                                        } else {
                                            if(draft.investigations[uuidToString(inv.id)].end_time < inv.end_time){
                                                draft.investigations[uuidToString(inv.id)] = inv;
                                            }
                                        }

                                    }
                                } else {
                                    //this.filterAlarmingSensors(inv);
                                    if(draft.investigations[uuidToString(inv.id)] === undefined){
                                        draft.investigations[uuidToString(inv.id)] = inv;
                                    } else {
                                        //console.log('list end_time ' +draft.investigations[uuidToString(inv.id)].end_time)
                                        //console.log('data end_time ' +inv.end_time)
                                        if(draft.investigations[uuidToString(inv.id)].end_time < inv.end_time){
                                            draft.investigations[uuidToString(inv.id)] = inv;
                                        }
                                    }
                                }
                            }
                        }

                    }));
                } else {
                    //console.log(message)
                    if ('device' in message){
                        if (message.device !== undefined) {
                            const deviceName = message.device;
                            this.setState(produce(draft => {
                                if (this.state.devices[deviceName] === undefined) {
                                    draft.devices[deviceName] = new Device(deviceName);
                                }
                                draft.devices[deviceName] = draft.devices[deviceName].update(message);
                            }));
                        }
                    }
                //Disable investigation parsing
                //this.updateInvestigations();
                }
                /*
                this.setState({timeOffset:new Date().getTime() - parseInt(header.timestamp)});
                console.log('TimeOffset: '+ (new Date().getTime() - parseInt(header.timestamp)));
                */
            }
        });
    }

    handleHealthMessage(message:messages.Core.GroupedHealthMessage){
        this.setState(produce(draft => {
            draft.health= draft.health.update(message);
        }));
        this.setState({timeOffset:new Date().getTime() - message.time});
        //console.log('Message Time: '+message.time+' TimeOffset: '+ (new Date().getTime() - message.time));
    }

    handleLocationMessage(message:GroupedLocationMessage){
        this.setState(produce(draft => {
            draft.health= draft.health.updateLocation(message);
        }));
        //this.setState({timeOffset:new Date().getTime() - message.time});
    }

    handleDoseMapMessage(message:DoseMapMessage,key?: string){
        console.log('handleDoseMapMessage')
        if(key === undefined)
            key = 'A';
        let message_changed = true;
        if(message.lastUpdatedTime !== null && this.state.doseMaps[key] !==undefined){
            if(this.state.doseMaps[key]?.lastUpdatedTime !== null && this.state.doseMaps[key]?.lastUpdatedTime !== undefined){
                if(message.lastUpdatedTime === this.state.doseMaps[key]?.lastUpdatedTime!){
                    message_changed = false;
                }
            }
        }
        if(message_changed){
            console.log('***MESSAGE CHANGED')
            //if(this.state.doseMapType){
                console.log('doseMapType being displayed: '+this.state.doseMapType)
                this.setState(produce(draft => {
                        draft.doseMapBusy['MAP'] = {flag:true,percent:-1};
                }));
            //}

            setTimeout(()=>{
                message = DoseMapUtility.updateDataStatistics(message);

                /*
                if(this.getAnyScaleOverrideByType(key!)){
                    console.log('Carryover scale override')
                    const thisScaleOverrideByType = this.getScaleOverrideByType(key!);
                    DoseMapUtility.resetDisplayStatisticsToData(message,thisScaleOverrideByType);
                } else {
                    DoseMapUtility.resetDisplayStatisticsToData(message);
                }
                */
                this.setState(produce(draft => {
                    let messages = [];
                    let messageA:DoseMapMessage|undefined;
                    let messageB:DoseMapMessage|undefined;
                    if(key==='A'){
                        messageA = message;
                        messageB = draft.doseMaps['B'];
                    } else if(key==='B'){
                        messageA = draft.doseMaps['A'];
                        messageB = message
                    }
                    messages.push(messageA);
                    messages.push(messageB);

                    const globalMinMax = DoseMapUtility.getGlobalDataMinMax(messages);
                    if(messageA){
                        messageA.globalScaleVal = globalMinMax;
                        messageA = DoseMapUtility.setDisplayMinMax(messageA,globalMinMax);
                        messageA = DoseMapUtility.updateDoseMaps(messageA);
                        draft.doseMaps['A'] = messageA;
                    }
                    if(messageB){
                        messageB.globalScaleVal = globalMinMax;
                        messageB = DoseMapUtility.setDisplayMinMax(messageB,globalMinMax);
                        messageB = DoseMapUtility.updateDoseMaps(messageB);
                        draft.doseMaps['B'] = messageB;
                    }
                    //this.setState(produce(draft => {
                    //draft.doseMaps[key!] = message;
                    draft.doseMapBusy[key!] = {flag:false,percent:-1};

                    draft.doseMapBusy['MAP'] = {flag:false,percent:-1};
                }));
            },100);
        }
    }

    handleDoseMapLoadProgress(key: string, flag: boolean, percent?: number) {
        //console.log('handleDoseMapLoadProgress')
        //console.log('set busy for '+key+' '+flag+' '+percent);
        this.setState(produce(draft => {
            draft.doseMapBusy[key].flag = flag;
            if(flag){
                if(percent){
                    draft.doseMapBusy[key].percent = percent;
                }
            } else {
                draft.doseMapBusy[key].percent = -1;
            }
        }));
        this.render();
    }

    componentDidMount() {
        document.title = "PSI REACT";

        let radFilterSetting;
        if(API_ROOT.Modalities.Rad){
            if(uiStorage.getStoredUISetting('modalityFilterSettingRad')!==undefined)
                radFilterSetting = uiStorage.getStoredUISetting('modalityFilterSettingRad')
            else
            radFilterSetting = ModalitySelectEnum.ALL
        }
        let chemFilterSetting;
        if(API_ROOT.Modalities.Chem){
            if(uiStorage.getStoredUISetting('modalityFilterSettingChem')!==undefined)
                chemFilterSetting = uiStorage.getStoredUISetting('modalityFilterSettingChem')
            else
                chemFilterSetting = ModalitySelectEnum.ALL
        }
        let bioFilterSetting;
        if(API_ROOT.Modalities.Bio){
            if(uiStorage.getStoredUISetting('modalityFilterSettingBio')!==undefined)
                bioFilterSetting = uiStorage.getStoredUISetting('modalityFilterSettingBio')
            else
                bioFilterSetting = ModalitySelectEnum.ALL
        }

        let modalityFilterSetting = {rad:radFilterSetting, chem:chemFilterSetting, bio:bioFilterSetting}
        this.setState({modalityFilterSetting:modalityFilterSetting,invTypeToShow: getInvToShowFromModalityFilterSetting(modalityFilterSetting)});

        if (this.context?.jwtToken){
            this.dataStream.connect(this.context?.jwtToken);
            this.healthStream.connect(this.context?.jwtToken);
            this.encounterStream.connect(this.context?.jwtToken);
            this.locationStream.connect(this.context?.jwtToken);
            if(API_ROOT.Modalities.RadDoseMap){
                this.doseMapStream.connect(this.context?.jwtToken);
            }
        }
        if(uiStorage.getStoredUISetting('showSuppressedAlerts')!==undefined){
            this.setState({showSuppressedAlerts: uiStorage.getStoredUISetting('showSuppressedAlerts')})
        }
    }

    componentWillUnmount() {
        this.dataStream.disconnect();
        this.healthStream.disconnect();
        this.encounterStream.disconnect();
        this.locationStream.disconnect();
        if(API_ROOT.Modalities.RadDoseMap)
            this.doseMapStream.disconnect();

        /*
        if (this.encounterRefreshTimer !== null) {
            clearTimeout(this.encounterRefreshTimer);
            this.encounterRefreshTimer = null;
        }
        */
    }

    normalizeReplayTime(time: number) {
        if (this.state.replayInvestigation === undefined)
            return undefined;

        const replayInvestigation = this.state.investigations[this.state.replayInvestigation];
        let thisInvestigationPadding = this.investigationPadding;
        if(replayInvestigation && 'type' in replayInvestigation){
            if(replayInvestigation.type === messages.Encounter.EncounterType.BIOSIGMA || replayInvestigation.type === messages.Encounter.EncounterType.CHEMSIGMA){
                thisInvestigationPadding = 5*60000;
            }
        }
        if (time < replayInvestigation.start_time - thisInvestigationPadding)
            time = replayInvestigation.start_time - thisInvestigationPadding;
        else if (time > replayInvestigation.end_time + thisInvestigationPadding)
            time = replayInvestigation.end_time + thisInvestigationPadding;

        return time;
    }

    getAnyScaleOverrideByType(key:string){
        const thisScaleOverrideByType = this.getScaleOverrideByType(key);
        let result = false;
        if((thisScaleOverrideByType[DoseDatatype.DOSE_RATE].min && thisScaleOverrideByType[DoseDatatype.DOSE_RATE].minVal) ||
           (thisScaleOverrideByType[DoseDatatype.DOSE_RATE].max && thisScaleOverrideByType[DoseDatatype.DOSE_RATE].maxVal) ||
           (thisScaleOverrideByType[DoseDatatype.MAX_DOSE_RATE].min && thisScaleOverrideByType[DoseDatatype.MAX_DOSE_RATE].minVal) ||
           (thisScaleOverrideByType[DoseDatatype.MAX_DOSE_RATE].max && thisScaleOverrideByType[DoseDatatype.MAX_DOSE_RATE].maxVal) ||
           (thisScaleOverrideByType[DoseDatatype.UNCERTAINTY].min && thisScaleOverrideByType[DoseDatatype.UNCERTAINTY].minVal) ||
           (thisScaleOverrideByType[DoseDatatype.UNCERTAINTY].max && thisScaleOverrideByType[DoseDatatype.UNCERTAINTY].maxVal)){
               result = true;
           }
        return result;
    }

    getScaleOverrideByType(key:string){
        const thisScaleOverrideByType = {[DoseDatatype.DOSE_RATE]:this.state.doseMapScaleOverride[DoseDatatype.DOSE_RATE][key],
                                    [DoseDatatype.MAX_DOSE_RATE]:this.state.doseMapScaleOverride[DoseDatatype.MAX_DOSE_RATE][key],
                                    [DoseDatatype.UNCERTAINTY]:this.state.doseMapScaleOverride[DoseDatatype.UNCERTAINTY][key]};
        return(thisScaleOverrideByType);

    }


    componentDidUpdate(prevProps: {}, prevState: AppState) {
        const liveStream = !this.state.replayInvestigation;
        if(liveStream && this.state.replayInvestigation !== prevState.replayInvestigation){
            this.setState({ replayTime:undefined});
        }
        if (liveStream && this.state.investigationData !== undefined) {
            this.setState({ investigationData: undefined, selectedDevicesReplay:{}});
        } else if (this.state.replayInvestigation !== prevState.replayInvestigation) {
            this.setState({ selectedDevicesReplay:{} });

            this.loadEncounterData();
        }

        //Historical Encounter query
        if(this.state.streamStatus.encounter && !this.encounterHistoryQueried){
            //const end_timestamp = 1655402280000; //1655404200000; //Date.now();
            let end_timestamp = Date.now(); //-(3600000*4);
            let start_timestamp = end_timestamp-3600000 * this.loadPastEncounters; // Past Encounters

            //start_timestamp = 1713801205000
            //end_timestamp = start_timestamp+30000

            //HACK to display data timeframe with ADA data
            //const end_timestamp = 1660766400000; //-(3600000*4);
            //const start_timestamp = end_timestamp-3600000 * this.loadPastEncounters; // Past Encounters

            console.log('Historical Encounter Query')

            //TODO add flag for historical encounter in queryEncounter
            const return_last_encounter_only = true;
            this.encounterStream.queryEncounter(start_timestamp,end_timestamp,return_last_encounter_only).then((encounters) => {

                //HACK TO ADD NETWORK ENCOUNTER
                /*
                if (encounters){
                    let thisEncounter = new messages.Encounter.Encounter();
                    thisEncounter.id = generateRandomId();
                    thisEncounter.node_name = 'TIER2';
                    thisEncounter.start_time = new Date(2025,0,13,12,0,1,1).getTime();
                    thisEncounter.end_time = new Date(2025,0,13,12,10,2,1).getTime();
                    thisEncounter.user_generated = false;
                    thisEncounter.finalized = true;
                    thisEncounter.type = messages.Encounter.EncounterType.CHEMSIGMA;
                    thisEncounter.source_location = new messages.Rad.Location();
                    thisEncounter.source_location.latitude = 40.752737;
                    thisEncounter.source_location.longitude = -73.977057;
                    thisEncounter.current_glrt = 130.0;
                    thisEncounter.max_glrt = 431.0;
                    let this_sensor = new SensorHealthData();
                    this_sensor.device='PSI TIER2';
                    this_sensor.make='PSI';
                    this_sensor.make_raw ='PSI';
                    this_sensor.model = 'BIOSIGMA';
                    this_sensor.serial = '';
                    thisEncounter.algorithm_name = 'PSI CBSIGMA';
                    thisEncounter.chemicals = new messages.SigmaPlusChem.ChemicalIdentification();
                    thisEncounter.chemicals.identifications = [new messages.SigmaPlusChem.ChemicalTargetIdentification({
                        concentration: 21,
                        chemical: messages.SigmaPlusChem.SigmaPlusChemicalTarget.BY_CAS_REGISTRY_NUMBER,
                        cas_registry_number: 'Acetone'
                    })];
                    //thisEncounter.alarming_sensors = [];
                    //thisEncounter.alarming_sensors.push(this_sensor);
                    encounters.push(thisEncounter);
                }
                */

                if(encounters !== undefined){
                    const undismissed = invStorage.syncInvestigationList(encounters);
                    const collapsed = invStorage.collapsedInvestigations(encounters);
                    this.setState(produce(draft => {
                        undismissed.forEach(encounter => {
                            //Close any old unfinalized alerts
                            if(!encounter.finalized){
                                if((new Date().getTime() - encounter.end_time) > this.closeUnfinalizedInv){
                                //if((new Date().getTime() - encounter.end_time) > 360000){ //360000){
                                    console.log('finalizing '+uuidToString(encounter.id)+' '+(new Date().getTime() - encounter.end_time)/1000+' sec elapsed')
                                    encounter.finalized = true;
                                }
                            }
                            //not suppressed or show all alerts
                            if(draft.showSuppressedAlerts || encounter.suppressed !== true){
                                //Filter any inv that are not in the health node list
                                if(this.isInvestigationInNodeList(encounter)){
                                    if(this.hideTier0Encounters){
                                        //if(!this.isTier0OnlyEncounter(encounter)){
                                        //    this.filterAlarmingSensors(encounter);
                                        //    draft.investigations[uuidToString(encounter.id)] = encounter;
                                        //}
                                        if(!this.isSensorLevelEncounter(encounter)){
                                            //this.filterAlarmingSensors(encounter);
                                            draft.investigations[uuidToString(encounter.id)] = encounter;
                                        }
                                    } else {
                                        this.filterAlarmingSensors(encounter);
                                        draft.investigations[uuidToString(encounter.id)] = encounter;
                                    }
                                }
                            }


                        });
                        collapsed.forEach(encounter => {
                            draft.investigationsCollapsed[uuidToString(encounter.id)] = encounter;
                        });

                    }))

                    /*
                    encounters.forEach((encounter,idx) =>{
                        console.log("idx: "+idx+" "+uuidToString(encounter.id)+' type: '+encounter.type+')
                    })
                    */
                }
            });

            this.encounterHistoryQueried = true;

        }

        //Dose Map Summary Query
        if(this.state.streamStatus.doseMap && !this.doseMapSummaryQueried){

            console.log('Dose Map Summary Query')

            //TODO add flag for historical encounter in queryEncounter
            this.doseMapStream.getDoseMapSummary().then((doseMapSummaryList) => {
                console.log('doseMapSummaryList:')
                console.log(doseMapSummaryList)
                if(doseMapSummaryList){
                    this.setState(produce(draft => {
                        draft.doseMapDateAvailable = doseMapSummaryList;
                    }));
                }
            });
            this.doseMapSummaryQueried = true;
        }

        //Change in DoseMap Date selection
        if(this.state.doseMapDateSelected !== prevState.doseMapDateSelected){
            console.log('DoseMapDateSelected Changed')
            const doseMapDateSelectedKeys = Object.keys(this.state.doseMapDateSelected)
            doseMapDateSelectedKeys.forEach(key => {
                if(this.state.doseMapDateSelected[key] !== prevState.doseMapDateSelected[key]){
                    console.log('key changed: '+key)
                    console.log('clear data')
                    this.doseMapStream.clearDoseMap(key);
                    this.setState(produce(draft => {
                            draft.doseMaps[key] = undefined;
                    }));
                    //Clear type if selected for this map
                    if(this.state.doseMapSelected === key){
                        this.setState(produce(draft => {
                                //draft.doseMapType = undefined;
                        }));
                    }
                    this.setState(produce(draft => {
                            draft.doseMaps[key] = undefined;
                    }));

                    //Clear override and ganging

                    this.setState(produce(draft => {
                        for(const thisType of [DoseDatatype.DOSE_RATE,DoseDatatype.MAX_DOSE_RATE,DoseDatatype.UNCERTAINTY]){
                            if(key === 'C'){
                                draft.doseMapScaleOverride[thisType]['C'].min = false;
                                draft.doseMapScaleOverride[thisType]['C'].max = false;
                                draft.doseMapScaleOverride[thisType]['C'].minVal = undefined;
                                draft.doseMapScaleOverride[thisType]['C'].maxVal = undefined;
                                DoseMapUtility.resetDisplayStatisticsToData(draft.doseMaps['C']);
                            } else {
                                for(const thisKey of ['A','B','GANG']){
                                    draft.doseMapScaleOverride[thisType][thisKey].min = false;
                                    draft.doseMapScaleOverride[thisType][thisKey].max = false;
                                    draft.doseMapScaleOverride[thisType][thisKey].minVal = undefined;
                                    draft.doseMapScaleOverride[thisType][thisKey].maxVal = undefined;
                                    if(key === 'A'){
                                        const thisScaleOverrideByType = this.getScaleOverrideByType('B');
                                        DoseMapUtility.resetDisplayStatisticsToData(draft.doseMaps['B']);
                                        draft.doseMapScaleOverride[thisType]['C'].min = false;
                                        draft.doseMapScaleOverride[thisType]['C'].max = false;
                                        draft.doseMapScaleOverride[thisType]['C'].minVal = undefined;
                                        draft.doseMapScaleOverride[thisType]['C'].maxVal = undefined;;   //If new date is selected then also reset result
                                    } else if(key === 'B'){
                                        const thisScaleOverrideByType = this.getScaleOverrideByType('A');
                                        DoseMapUtility.resetDisplayStatisticsToData(draft.doseMaps['A']);
                                        draft.doseMapScaleOverride[thisType]['C'].min = false;
                                        draft.doseMapScaleOverride[thisType]['C'].max = false;
                                        draft.doseMapScaleOverride[thisType]['C'].minVal = undefined;
                                        draft.doseMapScaleOverride[thisType]['C'].maxVal = undefined;   //If new date is selected then also reset result
                                    }
                                }
                            }
                        }
                    }));

                    const thisDoseMapDateSelected = this.state.doseMapDateSelected[key];
                    if(thisDoseMapDateSelected===DoseMapLiveString && key==='A'){
                        console.log('ENABLE LIVE FEED')
                        this.doseMapStream.abortDoseMapQuery(key);
                        this.doseMapStream.setDoseMapLive(true);
                        this.setState(produce(draft => {
                            draft.doseMapBusy[key].flag = true;
                            draft.doseMapBusy[key].percent = -1;
                        }))
                    } else if(thisDoseMapDateSelected.includes(DoseMapLoadFromFileString) || thisDoseMapDateSelected.includes(DoseMapLoadedFromFileString)){
                        if(key==='A'){
                            console.log('DISABLE LIVE FEED')
                            this.doseMapStream.setDoseMapLive(false);
                        }
                        this.setState(produce(draft => {
                            draft.doseMapBusy[key].flag = false;
                            draft.doseMapBusy[key].percent = -1;
                        }))
                        this.doseMapStream.abortDoseMapQuery(key);
                    } else if(thisDoseMapDateSelected===''){
                        //Everything unselected
                        if(key==='A'){
                            console.log('DISABLE LIVE FEED')
                            this.doseMapStream.setDoseMapLive(false);
                        }
                        this.setState(produce(draft => {
                            draft.doseMapBusy[key].flag = false;
                            draft.doseMapBusy[key].percent = -1;
                        }))
                        this.doseMapStream.abortDoseMapQuery(key);
                    } else {
                        if(key==='A'){
                            console.log('DISABLE LIVE FEED')
                            this.doseMapStream.setDoseMapLive(false);
                        }
                        if(!(thisDoseMapDateSelected.includes(DoseMapLoadFromFileString) || thisDoseMapDateSelected.includes(DoseMapLoadedFromFileString) ||thisDoseMapDateSelected === "")){
                            this.setState(produce(draft => {
                                draft.doseMapBusy[key].flag = true;
                                draft.doseMapBusy[key].percent = -1;
                            }))
                        }
                        this.doseMapStream.abortDoseMapQuery(key);
                        this.doseMapStream.getDoseMapFromHistory(key,thisDoseMapDateSelected);
                    }
                }
            });
        }
        //Change in Foreground or background or analysis method needs to update analysis
        if((this.state.doseMaps['A']?.lastUpdatedTime !== prevState.doseMaps['A']?.lastUpdatedTime) ||
           (this.state.doseMaps['B']?.lastUpdatedTime !== prevState.doseMaps['B']?.lastUpdatedTime) ||
           (this.state.doseMapAnalysisType !== prevState.doseMapAnalysisType)){

           this.setState(produce(draft => {
               draft.doseMapBusy['C'].flag = true;
               draft.doseMapBusy['C'].percent = -1;
               draft.doseMapBusy['MAP'] = {flag:true,percent:-1};
           }))
           setTimeout(()=>{
            this.setState(produce(draft => {
                if (draft.doseMaps['A'] && draft.doseMaps['B'] && draft.doseMapAnalysisType) {

                    let diffmessage = DoseMapUtility.UpdateDoseMapAnalysis(draft.doseMapAnalysisType,draft.doseMaps['A'], draft.doseMaps['B']);
                    diffmessage = DoseMapUtility.updateDataStatistics(diffmessage);

                    if(this.getAnyScaleOverrideByType('C')){
                        console.log('Carryover scale override');
                        const thisScaleOverrideByType = this.getScaleOverrideByType('C');
                        DoseMapUtility.resetDisplayStatisticsToData(diffmessage,thisScaleOverrideByType);
                    } else {
                        DoseMapUtility.resetDisplayStatisticsToData(diffmessage);
                    }

                    diffmessage = DoseMapUtility.updateDoseMaps(diffmessage);
                    draft.doseMaps['C'] = diffmessage;

               } else {
                   draft.doseMaps['C'] = undefined;
               }
               draft.doseMapBusy['C'].flag = false;
               draft.doseMapBusy['C'].percent = -1;
               draft.doseMapBusy['MAP'] = {flag:false,percent:-1};
            }))
        },100);
        /*
            this.setState(produce(draft => {
                draft.doseMapBusy['C'].flag = false;
                draft.doseMapBusy['C'].percent = -1;
                draft.doseMapBusy['MAP'] = {flag:false,percent:-1};
            }))
            */

        }


        if(this.state.doseMapBusy !== prevState.doseMapBusy){
            //console.log('doseMapBusy changed')
            /*
            console.log(this.state.doseMapBusy['A'])
            console.log(this.state.doseMapBusy['B'])
            console.log(this.state.doseMapBusy['C'])
            console.log(this.state.doseMapBusy['MAP'])
            */
        }

        //Update sensor data to request
        if(this.state.selectedDevicesRealtime !== prevState.selectedDevicesRealtime && liveStream){
            //console.log('Plot device changed');
            let sensors = Object.values(this.state.selectedDevicesRealtime);
            this.dataStream.setSensorsForDataRequest(sensors);
        }
        //TODO for selectedDevicesReplay
        if(this.state.selectedDevicesReplay !== prevState.selectedDevicesReplay && !liveStream){
            console.log('Plot device changed in REPLAY');
            //TODO
            this.loadAdditionalReplayData();
        }

        //Check for any current BIOSIGMA alerts and if so, get CBSIGMA messages
        const currentOngoingBioSIGMAInv = this.getInvOngoingCBSIGMA(this.state.investigations);
        const prevOngoingBioSIGMAInv = this.getInvOngoingCBSIGMA(prevState.investigations);
        if(currentOngoingBioSIGMAInv.length !== prevOngoingBioSIGMAInv.length){
            if(currentOngoingBioSIGMAInv.length > 0){
                console.log('**NEW CBSIGMA ALERT')
                let sensors = Object.values(this.state.selectedDevicesRealtime);
                let alreadyDisplayed = false;
                sensors.forEach(sensor=> {
                    if(sensor.sensorDisplayed.includes('PSI CBSIGMA')){
                        alreadyDisplayed = true;
                    }
                });
                if(!alreadyDisplayed){
                    console.log('**GETTING LIVE PSI CBSIGMA during CBSIGMA alert')
                    let temp_selection:SensorDisplaySelection = {deviceDisplayed:BD21DataStream.remapDeviceName('PSI'),
                                                                     device_name:'PSI',
                                                                     sensorDisplayed:'PSI CBSIGMA',
                                                                     sensor_name:'CBSigmaOutput',
                                                                     plot1Displayed:PlottableSensorReading.getDefaultPlot1(PlottableSensorTypes.CBSIGMA),
                                                                     plot2Displayed:PlottableSensorReading.getDefaultPlot2(PlottableSensorTypes.CBSIGMA)}
                    sensors.push(temp_selection)
                    this.dataStream.setSensorsForDataRequest(sensors);
                } else {
                    console.log('PSI CBSIGMA already being displayed')
                }

            } else {
                console.log('**NO ONGOING CBSIGMA alert')
                let sensors = Object.values(this.state.selectedDevicesRealtime);
                this.dataStream.setSensorsForDataRequest(sensors);
            }
        }

        //DoseMap scale change
        if(this.state.doseMapSelected !== undefined){
            let doseMapsToUpdate = ['A','B','C'];
            /*
            let doseMapsToUpdate = [this.state.doseMapSelected];
            if(this.state.doseMapScaleOverride[DoseDatatype.DOSE_RATE]['GANG'].max || this.state.doseMapScaleOverride[DoseDatatype.DOSE_RATE]['GANG'].min ||
                this.state.doseMapScaleOverride[DoseDatatype.MAX_DOSE_RATE]['GANG'].max || this.state.doseMapScaleOverride[DoseDatatype.MAX_DOSE_RATE]['GANG'].min){
                    console.log('GANGED')
                    doseMapsToUpdate = ['A','B','C'];
                }
                */
            for(const thisDoseMapSelected of doseMapsToUpdate){

            if(this.state.doseMaps[thisDoseMapSelected] !== undefined && prevState.doseMaps[thisDoseMapSelected] !== undefined){
                let this_state_doseMap = this.state.doseMaps[thisDoseMapSelected]!;
                let prev_state_doseMap = prevState.doseMaps[thisDoseMapSelected]!;
                if(this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].max !== prev_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].max ||
                    this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].min !== prev_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].min ||
                    this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max !== prev_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max ||
                    this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min !== prev_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min ||
                    this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].max !== prev_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].max ||
                    this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].min !== prev_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].min ){
                        console.log('Updating: '+thisDoseMapSelected)
                        let reset_dose = false;
                        if(this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].min !== null && this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].max !== null){
                            if(this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].min! < this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].max!){
                                console.log('Dose Map scale changed: '+this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].min+' '+this_state_doseMap.displayScaleVal[DoseDatatype.DOSE_RATE].max)
                            } else {
                                console.log('RESET DOSE SCALE')
                                reset_dose = true;
                            }
                        }
                        let reset_dose_max = false;
                        if(this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min !== null && this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max !== null){
                            if(this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min! < this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max!){
                                console.log('Dose max Map scale changed: '+this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min+' '+this_state_doseMap.displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max)
                            } else {
                                console.log('RESET DOSE MAX SCALE')
                                reset_dose_max = true;
                            }
                        }
                        let reset_uncertainty = false;
                        if(this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].min !== null && this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].max !== null){
                            if(this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].min! < this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].max!){
                                console.log('Dose Uncertainty scale changed: '+this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].min+' '+this_state_doseMap.displayScaleVal[DoseDatatype.UNCERTAINTY].max)
                            } else {
                                console.log('RESET UNCERTAINTY SCALE')
                                reset_uncertainty = true;
                            }
                        }
                        this.render();
                        this.setState(produce(draft => {
                                if(reset_dose){
                                    draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.DOSE_RATE].min = draft.doseMaps[thisDoseMapSelected].dataScaleVal[DoseDatatype.DOSE_RATE].min;
                                    draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.DOSE_RATE].max = draft.doseMaps[thisDoseMapSelected].dataScaleVal[DoseDatatype.DOSE_RATE].max;
                                }
                                if(reset_dose_max){
                                    draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min = draft.doseMaps[thisDoseMapSelected].dataScaleVal[DoseDatatype.MAX_DOSE_RATE].min;
                                    draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max = draft.doseMaps[thisDoseMapSelected].dataScaleVal[DoseDatatype.MAX_DOSE_RATE].max;
                                }
                                if(reset_uncertainty){
                                    draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.UNCERTAINTY].min = draft.doseMaps[thisDoseMapSelected].dataScaleVal[DoseDatatype.UNCERTAINTY].min;
                                    draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.UNCERTAINTY].max = draft.doseMaps[thisDoseMapSelected].dataScaleVal[DoseDatatype.UNCERTAINTY].max;
                                }

                                this.doseMapStream.setDoseMapSetting(DoseDatatype.DOSE_RATE,draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.DOSE_RATE].max,draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.DOSE_RATE].min);
                                this.doseMapStream.setDoseMapSetting(DoseDatatype.MAX_DOSE_RATE,draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.MAX_DOSE_RATE].max,draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.MAX_DOSE_RATE].min);
                                this.doseMapStream.setDoseMapSetting(DoseDatatype.UNCERTAINTY,draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.UNCERTAINTY].max,draft.doseMaps[thisDoseMapSelected].displayScaleVal[DoseDatatype.UNCERTAINTY].min);
                                //console.log("TODO handle DoseMapMessage")
                                //console.log('updating map after dosemap setting change '+thisDoseMapSelected)
                                const message = DoseMapUtility.updateDoseMaps(draft.doseMaps[thisDoseMapSelected]);

                                draft.doseMaps[thisDoseMapSelected] = message;
                        }));
                }
            }
        }
        }

    }
    getInvOngoingCBSIGMA(investigations:{[key:string]:Investigation}){
        return Object.values(investigations).filter((inv)=>{
                if('type' in inv){
                    if((inv.type === messages.Encounter.EncounterType.BIOSIGMA || inv.type === messages.Encounter.EncounterType.CHEMSIGMA) && !inv.finalized){
                        return true;
                    }
                }
                return false;
            });
    }
    /*
    handleCheckEncounterForUnfinalized(){
        console.log("handleCheckEncounterForUnfinalized")
        const undismissed = Object.values(this.state.investigations);
        if(undismissed !== undefined){
            this.setState(produce(draft => {
                undismissed.forEach(encounter => {
                    if(!encounter.finalized){
                        if((new Date().getTime() - encounter.end_time) > 60000){ //360000){
                            console.log('finalizing '+uuidToString(encounter.id)+' '+(new Date().getTime() - encounter.end_time)/1000+' sec elapsed')
                            encounter.finalized = true;
                        }
                    }
                    //this.filterAlarmingSensors(encounter);
                    draft.investigations[uuidToString(encounter.id)] = encounter;
                });
            }));
        }
        this.encounterRefreshTimer = setTimeout(() => {
            this.handleCheckEncounterForUnfinalized()
        }, 60000)
    }
    */

    isTier0OnlyEncounter(inv:Investigation){
        inv = inv as messages.Encounter.Encounter;
        if(inv.type === messages.Encounter.EncounterType.BIOSIGMA ||
           inv.type === messages.Encounter.EncounterType.CHEMSIGMA ||
           inv.type === messages.Encounter.EncounterType.RN_FUSION){
            return false;
        } else {
            let return_flag = false;
            if(inv.alarming_sensors.length === 1){
            inv.alarming_sensors.forEach(alarming_sensor => {
                    if((alarming_sensor.model==='IBAC_ML_Tier0_Bio')){
                        if(this.hideTier0Encounters){
                            return_flag = true;
                        }
                    } else {
                        return_flag = false
                    }
                });
            }
            return return_flag;
        }

    }
    isSensorLevelEncounter(inv:Investigation){
        inv = inv as messages.Encounter.Encounter;
        if(inv.type === messages.Encounter.EncounterType.BIOSIGMA ||
           inv.type === messages.Encounter.EncounterType.CHEMSIGMA ||
           inv.type === messages.Encounter.EncounterType.RN_FUSION){
            return false;
        } else {
            let return_flag = true;
            /*
            if(inv.alarming_sensors.length === 1){
            inv.alarming_sensors.forEach(alarming_sensor => {
                    if((alarming_sensor.model==='IBAC_ML_Tier0_Bio')){
                        if(this.hideTier0Encounters){
                            return_flag = true;
                        }
                    } else {
                        return_flag = false
                    }
                });
            }
            */
            return return_flag;
        }

    }
    filterAlarmingSensors(encounter:Investigation){
        if('type' in encounter){
            if(encounter.type !== messages.Encounter.EncounterType.BIOSIGMA &&
               encounter.type !== messages.Encounter.EncounterType.CHEMSIGMA &&
               encounter.type !== messages.Encounter.EncounterType.RN_FUSION){
                let filtered_alarming_sensors:Core.SensorDefinition[] = [];
                if(encounter.alarming_sensors !== null){
                    encounter.alarming_sensors.forEach(alarming_sensor => {
                        if((alarming_sensor.model==='IBAC_ML_Tier0_Bio')){
                            if(this.getTier0Tier2Results && !this.hideTier0Encounters){
                                filtered_alarming_sensors.push(alarming_sensor);
                            }
                        } else {
                            filtered_alarming_sensors.push(alarming_sensor);
                        }
                    });

                    encounter.alarming_sensors = filtered_alarming_sensors;
                }
            }
        }
    }

    //HACK
    generateRandomId() {
        return new messages.Core.UUID({
            leastSignificantBits: new thrift.Int64(window.crypto.getRandomValues(new Buffer(8))) as unknown as number,
            mostSignificantBits: new thrift.Int64(window.crypto.getRandomValues(new Buffer(8))) as unknown as number
        });
    }
    //// HACK:
    calculateCurrentSensorPositions() {
        let sensorPositions: { [key: string]: SensorPosition } = {};
        Array.from(this.state.health.nodes.keys()).map(hkey => {
            const thisNode = this.state.health.nodes.get(hkey);
            if (thisNode !== undefined){
                if(thisNode.latitude !== null && thisNode.longitude !== null){
                    sensorPositions[hkey] = { timestamp: this.state.health.time.getTime(), device: hkey, latitude: thisNode.latitude, longitude: thisNode.longitude, level:thisNode.level }
                }
            }

        });
        /*
        Object.keys(this.state.devices).map((dkey, dindex, darray) => {
            let max_timestamp = 0;
            let max_index;
            let max_pkey;
            this.state.devices[dkey].metReadings.forEach((reading, index) => {
                if (reading.timestamp > max_timestamp) {
                    max_index = index;
                    max_timestamp = reading.timestamp;
                }
            });
            if (max_index){
                const thisLat = this.state.devices[dkey].positions[max_index].latitude;
                const thisLon = this.state.devices[dkey].positions[max_index].longitude;
                sensorPositions[dkey] = { timestamp: max_timestamp, device: dkey, latitude: thisLat, longitude: thisLon }
            }

        });
        */
        return sensorPositions;
    }

    loadAdditionalReplayData() {
        if (this.state.replayInvestigation === undefined)
            return
        const replay = this.state.investigations[this.state.replayInvestigation];

        let thisInvestigationPadding = this.investigationPadding;
        if('type' in replay){
            if(replay.type === messages.Encounter.EncounterType.BIOSIGMA || replay.type === messages.Encounter.EncounterType.CHEMSIGMA){
                thisInvestigationPadding = 5*60000;
            }
        }
        let start_time = replay.start_time - thisInvestigationPadding;
        let end_time = replay.end_time + thisInvestigationPadding;

        Object.values(this.state.selectedDevicesReplay).forEach( selection => {
            if(selection.device_name !== '' && selection.sensor_name !== ''){
                const this_deviceDisplayed = selection.deviceDisplayed;
                const this_sensorDisplayed = ConvertSensorNameToMakeModel(selection.sensorDisplayed);
                console.log(this_deviceDisplayed +' '+this_sensorDisplayed)
                if(this.state.investigationData !== undefined){

                    let this_device = this.state.investigationData[this_deviceDisplayed];
                    if(this_device === undefined || this_device.plottableSensorReadings === undefined || this_device.plottableSensorReadings[this_sensorDisplayed] === undefined){

                        let this_device_readings:PlottableSensorReading[] = [];

                        this.dataStream.queryHistoricalData(selection.device_name,selection.sensor_name,this_deviceDisplayed,this_sensorDisplayed,start_time,end_time).then(result => {
                              console.log(result)
                              let attrTime = replay.start_time;
                              if(result !== undefined && result[this_deviceDisplayed] !== undefined){
                                  if(result[this_deviceDisplayed].plottableSensorReadings !== undefined){
                                      this_device_readings = result[this_deviceDisplayed].plottableSensorReadings[this_sensorDisplayed];
                                  }
                                  if(this.state.investigationData !== null && this.state.investigationData !== undefined){
                                      this.setState(produce(draft => {
                                          if(draft.investigationData !== undefined){
                                              if (draft.investigationData[this_deviceDisplayed] === undefined) {
                                                  draft.investigationData[this_deviceDisplayed] = new Device(this_deviceDisplayed);
                                              }
                                              draft.investigationData[this_deviceDisplayed].plottableSensorReadings[this_sensorDisplayed] = this_device_readings;
                                              //console.log(this_deviceDisplayed+' '+this_sensorDisplayed)
                                              draft.investigationData[this_deviceDisplayed].dataStatus[this_sensorDisplayed] = result[this_deviceDisplayed].dataStatus[this_sensorDisplayed];
                                          }
                                      }));
                                  }
                              }
                        });
                    }

                } else {
                    //no initial investigationData
                        let this_device_readings:PlottableSensorReading[] = [];

                        this.dataStream.queryHistoricalData(selection.device_name,selection.sensor_name,this_deviceDisplayed,this_sensorDisplayed,start_time,end_time).then(result => {
                              console.log(result)
                              let attrTime = replay.start_time;
                              if(result !== undefined && result[this_deviceDisplayed] !== undefined){
                                  if(result[this_deviceDisplayed].plottableSensorReadings !== undefined){
                                      this_device_readings = result[this_deviceDisplayed].plottableSensorReadings[this_sensorDisplayed];
                                  }
                                  if(this.state.investigationData !== null && this.state.investigationData !== undefined){
                                      this.setState(produce(draft => {
                                          //if(draft.investigationData !== undefined){
                                              if (draft.investigationData[this_deviceDisplayed] === undefined) {
                                                  draft.investigationData[this_deviceDisplayed] = new Device(this_deviceDisplayed);
                                              }
                                              draft.investigationData[this_deviceDisplayed].plottableSensorReadings[this_sensorDisplayed] = this_device_readings;
                                              //console.log(this_deviceDisplayed+' '+this_sensorDisplayed)
                                              draft.investigationData[this_deviceDisplayed].dataStatus[this_sensorDisplayed] = result[this_deviceDisplayed].dataStatus[this_sensorDisplayed];
                                          //}
                                      }));
                                  } else {
                                      if(result !== undefined){
                                          this.setState({ investigationData: result, replayTime: attrTime });
                                      }
                                  }
                              }
                        });

                }
            }
        });
    }

    loadEncounterData() {
        if (this.state.replayInvestigation === undefined)
            return

        const replay = this.state.investigations[this.state.replayInvestigation];

        let thisInvestigationPadding = this.investigationPadding;
        if('type' in replay){

            if(HasCloudUI(replay.node_name)){

                //Spawn off to cloud UI display
                const thisTargetURL = GetCloudUIUrl(replay.node_name)
                if(thisTargetURL){
                    let thisInvUuid = this.state.replayInvestigation;
                    const thisParam:CloudUiDisplayParam = {isShowing:true,device:replay.node_name, url:thisTargetURL, invUuid:thisInvUuid}
                    this.setState(produce(draft => {
                        draft.cloudUiDisplayParam = {isShowing: true, device: thisParam.device, url:thisParam.url, invUuid:thisParam.invUuid};
                        draft.replayInvestigation = undefined;
                        draft.selectedInvestigation = thisInvUuid;
                    }));
                }
            } else {
                //Set selectedDevices for replay
                let thisSelectedDevices:{[key:string]:SensorDisplaySelection} = {};
                let keyindex = 0;
                if(replay.alarming_sensors !== null){
                    replay.alarming_sensors.forEach(sensor => {
                        const deviceDisplayed = BD21DataStream.remapDeviceName(replay.node_name);
                        const device_name = replay.node_name;
                        const sensorDisplayed = sensor.make+' '+sensor.model;
                        const sensor_name = sensor.make+'-'+sensor.model;
                        let plot1Displayed = '--';
                        let plot2Displayed = '--';
                        if(sensorDisplayed.includes('IBAC')){
                            plot1Displayed = PlottableSensorReading.getDefaultPlot1(PlottableSensorTypes.IBAC);
                            plot2Displayed = PlottableSensorReading.getDefaultPlot2(PlottableSensorTypes.IBAC);
                        } else if(sensorDisplayed.includes('TAC7')){
                            plot1Displayed = PlottableSensorReading.getDefaultPlot1(PlottableSensorTypes.TAC7);
                            plot2Displayed = PlottableSensorReading.getDefaultPlot2(PlottableSensorTypes.TAC7);
                        } else if(sensorDisplayed.includes('TACBIO')){
                            plot1Displayed = PlottableSensorReading.getDefaultPlot1(PlottableSensorTypes.TACBIO);
                            plot2Displayed = PlottableSensorReading.getDefaultPlot2(PlottableSensorTypes.TACBIO);
                        } else if(sensorDisplayed.includes('ADAv3')){
                            plot1Displayed = PlottableSensorReading.getDefaultPlot1(PlottableSensorTypes.ADAv3);
                            plot2Displayed = PlottableSensorReading.getDefaultPlot2(PlottableSensorTypes.ADAv3);
                        }
                        let selection:SensorDisplaySelection = {deviceDisplayed:deviceDisplayed,
                                                                         device_name:device_name,
                                                                         sensorDisplayed:sensorDisplayed,
                                                                         sensor_name:sensor_name,
                                                                         plot1Displayed:plot1Displayed,
                                                                         plot2Displayed:plot2Displayed
                                                                      }
                        const key = Date.now()+keyindex;
                         //JSON.parse(JSON.stringify(this.state.selectedDevicesReplay));
                        thisSelectedDevices[key] = selection;
                        keyindex++
                    });
                } else if(replay.type === messages.Encounter.EncounterType.BIOSIGMA || replay.type === messages.Encounter.EncounterType.CHEMSIGMA){
                    //Add cbsigma as one of the sensors
                    let selection:SensorDisplaySelection = {deviceDisplayed:BD21DataStream.remapDeviceName('PSI'),
                                                                     device_name:'PSI',
                                                                     sensorDisplayed:'PSI CBSIGMA',
                                                                     sensor_name:'CBSigmaOutput',
                                                                     plot1Displayed:PlottableSensorReading.getDefaultPlot1(PlottableSensorTypes.CBSIGMA),
                                                                     plot2Displayed:PlottableSensorReading.getDefaultPlot2(PlottableSensorTypes.CBSIGMA)
                                                                  }
                    const key = Date.now()+(Object.keys(thisSelectedDevices).length+1);
                     //JSON.parse(JSON.stringify(this.state.selectedDevicesReplay));
                    thisSelectedDevices[key] = selection;

                    //Investigation padding for BIOSIGMA = 5 minutes
                    thisInvestigationPadding = 5*60000;
                }

                this.setState({ selectedDevicesReplay: thisSelectedDevices });

                //this.addNetworkDeviceToSelectedDeviceSensor();

                this.dataStream.queryEncounterData(replay, thisInvestigationPadding).then(result => {
                    console.log(result)
                    let attrTime = replay.start_time;
                    if(result !== undefined){
                        this.setState({ investigationData: result, replayTime: attrTime });
                    }
                }).catch(reason => {
                    console.log(reason)
                    setTimeout(() => this.loadEncounterData(), 5000)
                })
            }
        }
    }

    isInvestigationInNodeList(inv:Investigation) {
        if(this.filterInvestigationsByNodeHealth){
            inv = inv as messages.Encounter.Encounter;
            if(inv.type === messages.Encounter.EncounterType.BIOSIGMA ||
               inv.type === messages.Encounter.EncounterType.CHEMSIGMA ||
               inv.type === messages.Encounter.EncounterType.RN_FUSION){
                return true;
            } else {
                let node_name = inv.node_name;
                node_name = BD21DataStream.remapDeviceName(node_name)
                node_name= node_name.replace(/_/g," ");
                if(this.state.health.nodes.get(node_name)){
                    return true;
                } else {
                    return false;
                }
            }
        } else {
            return true;
        }
    }

    updateInvestigations() {

    }

    getCBSigmaOutput(){
        let cbSigmaOutputArray;
        const liveStream = !this.state.replayInvestigation;
        if(liveStream){
            const deviceName = BD21DataStream.remapDeviceName('PSI');
            const sensorName = 'PSI CBSIGMA'; //Needs to match in ParseCBSigma and
            if(this.state.devices[deviceName] !== undefined){
                if(this.state.devices[deviceName].plottableSensorReadings[sensorName] !== undefined){
                    cbSigmaOutputArray = this.state.devices[deviceName].plottableSensorReadings[sensorName].flatMap((reading)=>{
                        if(reading.cbSigmaOutput !== null && reading.cbSigmaOutput !== undefined)
                            return [reading.cbSigmaOutput];
                        else
                            return []
                    })
                }
            }
            if(cbSigmaOutputArray)
                cbSigmaOutputArray = cbSigmaOutputArray.filter((item) => item !== undefined);
            if(cbSigmaOutputArray){
                let reverse_sorted = Object.values(cbSigmaOutputArray).sort(
                    (lhs, rhs) => rhs.timestamp - lhs.timestamp);

                if(reverse_sorted){
                    return [reverse_sorted[0]];
                } else {
                    return []
                }
            }
        } else {
            //console.log(this.state.investigationData)
            if(this.state.investigationData !== undefined){
                const deviceName = BD21DataStream.remapDeviceName('PSI');
                const sensorName = 'PSI CBSIGMA'; //Needs to match in ParseCBSigma and
                if(this.state.investigationData[deviceName] !== undefined){
                    if(this.state.investigationData[deviceName].plottableSensorReadings[sensorName] !== undefined){
                        cbSigmaOutputArray = this.state.investigationData[deviceName].plottableSensorReadings[sensorName].flatMap((reading)=>{
                            if(reading.cbSigmaOutput !== null && reading.cbSigmaOutput !== undefined)
                                return [reading.cbSigmaOutput];
                            else
                                return []
                        })
                    }
                }
            }
            if(cbSigmaOutputArray)
                cbSigmaOutputArray = cbSigmaOutputArray.filter((item) => item !== undefined);
        }


        return cbSigmaOutputArray;
    }

    render() {

        //console.log('App render @ '+Date.now())
        const liveStream = !this.state.replayInvestigation;
        const devices = liveStream ? this.state.devices : this.state.investigationData;
        let selectedDeviceSensor = liveStream ? this.state.selectedDeviceSensorRealtime : this.state.selectedDeviceSensorReplay;

        const sensorPositions = this.calculateCurrentSensorPositions();

        const displayModes = this.displayModes.map((mode) =>
            <option value={mode} key={mode}>{mode}</option>)

        const investigationInterface:InvestigationInterface = {
            select: (inv: Investigation) =>
                this.setState({ selectedInvestigation: uuidToString(inv.id) }),
            unselect: () => this.setState({ selectedInvestigation: undefined }),
            dismiss: (inv: Investigation) => {
                if (this.state.selectedInvestigation) {
                    const selected = this.state.investigations[this.state.selectedInvestigation];
                    if (selected.id === inv.id)
                        this.setState({ selectedInvestigation: undefined })
                }
                if (this.state.replayInvestigation) {
                    const selected = this.state.investigations[this.state.replayInvestigation];
                    if (selected.id === inv.id)
                        this.setState({ replayInvestigation: undefined })
                }
                invStorage.dismissInvestigation(inv);
                this.setState(produce(draft => { delete draft.investigations[uuidToString(inv.id)] }))
            },
            replay: (inv: Investigation) => {
                this.setState({
                    investigationData: undefined,
                    replayInvestigation: uuidToString(inv.id)
                });
            },
            unreplay: () => this.setState({
                replayInvestigation: undefined,
                selectedInvestigation: undefined,
                selectedDevicesReplay: {}
            }),
            dismissAll: () => {
                const inv_all = Object.values(this.state.investigations);
                inv_all.forEach((inv) => {
                    invStorage.dismissInvestigation(inv);
                    this.setState(produce(draft => { delete draft.investigations[uuidToString(inv.id)] }))
                    this.setState(produce(draft => { delete draft.investigationsNotAcknowledged[uuidToString(inv.id)] }))
                    this.setState(produce(draft => { delete draft.sourceAttributionSummaryNotAcknowledged[uuidToString(inv.id)] }))
                });
                this.setState({ selectedInvestigation: undefined });
                this.setState({ replayInvestigation: undefined });
            },
            finalize: (inv: Investigation) => {
                this.setState(produce(draft => { draft.investigations[uuidToString(inv.id)].finalized = true; }))
            },
            acknowledge: (inv: Investigation) => {
                /*
                    this.setState(produce(draft => { delete draft.sourceAttributionSummaryNotAcknowledged[uuidToString(inv.id)] }))
                    this.setState(produce(draft => { delete draft.investigationsNotAcknowledged[uuidToString(inv.id)] }))
                    if(inv.start_time > this.state.lastAcknowledged){
                        this.setState({lastAcknowledged: inv.start_time })
                    }
                    */
            },
            setAlertAcknowledge: (flag: boolean) => {
                /*
                this.setState({
                    alertAcknowledge: flag
                })
                */
            },
            setCollapse: (inv: Investigation, flag: boolean) => {
                invStorage.setCollapseInvestigation(inv, flag)
                if (flag) {
                    this.setState(produce(draft => { draft.investigationsCollapsed[uuidToString(inv.id)] = inv }))
                } else {
                    this.setState(produce(draft => { delete draft.investigationsCollapsed[uuidToString(inv.id)] }))
                }
            },
            setInvTypeToShow:(type:InvTypeToShow|null)=>{
                this.setState({
                        invTypeToShow: type
                })
            },
            queryDatastream: this.dataStream,

        }

        const selectedInvestigation = this.state.selectedInvestigation === undefined ? undefined :
            this.state.investigations[this.state.selectedInvestigation]
        const replayInvestigation = this.state.replayInvestigation === undefined ? undefined :
            this.state.investigations[this.state.replayInvestigation]
        const replayTime = this.state.replayTime ? new Date(this.state.replayTime) : undefined;

        const deviceInterface = {
            select: (deviceSensor:{device: string, sensor: string, follow: boolean}) => {
                if(liveStream){
                    this.setState({ selectedDeviceSensorRealtime: deviceSensor})
                } else {
                    this.setState({ selectedDeviceSensorReplay: deviceSensor})
                }
            },
            unselect: () => {
                if(liveStream){
                    this.setState({ selectedDeviceSensorRealtime: undefined })
                } else {
                    this.setState({ selectedDeviceSensorReplay: undefined })
                }
            },
            setPlotVisibility:(flag:boolean) => {
                this.setState({mapPlotVisibility:flag})
            },
            plotVisibility: this.state.mapPlotVisibility,
        }

        //const replayEncounter = replayInvestigation as messages.Encounter.Encounter;
        //const selectedDevicesReplay: {[key:string]: SensorDisplaySelection} = this.state.selectedDevicesRealtime;
        //selectedDisplayReplay['GCT West Platf']{deviceDisplayed:'West_Platform',sen};

        const plotSelectInterface:PlotSelectInterface = {
            selectedDevices: liveStream ? this.state.selectedDevicesRealtime : this.state.selectedDevicesReplay,
            update: (key: string, selection: SensorDisplaySelection) => {
                let thisSelectedDevices;
                if(liveStream){
                    thisSelectedDevices = JSON.parse(JSON.stringify(this.state.selectedDevicesRealtime));
                    thisSelectedDevices[key] = selection;
                    this.setState({ selectedDevicesRealtime: thisSelectedDevices });
                } else {
                    thisSelectedDevices = JSON.parse(JSON.stringify(this.state.selectedDevicesReplay));
                    thisSelectedDevices[key] = selection;
                    this.setState({ selectedDevicesReplay: thisSelectedDevices });
                }
            },
            remove: (key: string) => {
                let thisSelectedDevices;
                if(liveStream){
                    thisSelectedDevices = JSON.parse(JSON.stringify(this.state.selectedDevicesRealtime));
                    delete thisSelectedDevices[key];
                    this.setState({ selectedDevicesRealtime: thisSelectedDevices });
                } else {
                    thisSelectedDevices = JSON.parse(JSON.stringify(this.state.selectedDevicesReplay));
                    delete thisSelectedDevices[key];
                    this.setState({ selectedDevicesReplay: thisSelectedDevices });
                }

            },
            clear: () => {
                if(liveStream){
                    this.setState({ selectedDevicesRealtime: {}});
                } else {
                    this.setState({ selectedDevicesReplay: {}});
                }
            },
        }

        const modalityFilterInterface:ModalityFilterInterface = {
            currentSetting: this.state.modalityFilterSetting,
            set: (newSetting:ModalityFilterSetting) => {
                this.setState({
                    modalityFilterSetting: newSetting,
                    invTypeToShow: getInvToShowFromModalityFilterSetting(newSetting)
                });
                uiStorage.setStoredUISetting('modalityFilterSettingRad',newSetting.rad);
                uiStorage.setStoredUISetting('modalityFilterSettingChem',newSetting.chem);
                uiStorage.setStoredUISetting('modalityFilterSettingBio',newSetting.bio);
            }
        }

        let doseMapsAvailable:string[] = [];
        let doseMapsAvailableStats:{[key:string]:DoseMapStats} = {};
        Object.keys(this.state.doseMaps).forEach(element => {
            if(this.state.doseMaps[element] !== undefined){
                doseMapsAvailable.push(element);

                const thisDoseMap = this.state.doseMaps[element];
                if(thisDoseMap){
                    let thisDoseMapStats:DoseMapStats = {lastUpdatedTime: null,
                                                        dataScaleVal: {[DoseDatatype.DOSE_RATE]:{min:null,max:null},[DoseDatatype.MAX_DOSE_RATE]:{min:null,max:null},[DoseDatatype.UNCERTAINTY]:{min:null,max:null}}}

                    thisDoseMapStats.dataScaleVal = thisDoseMap.dataScaleVal;
                    thisDoseMapStats.lastUpdatedTime = thisDoseMap.lastUpdatedTime;
                doseMapsAvailableStats[element] = thisDoseMapStats;
                }

            }
        });
        //let doseMapDateAvailable: string[] = ['1732665600000','1732579200000','1732492800000'];

        let allowToHideAlertPins;
        if(this.state.doseMapControlOpen ||
            (!this.state.doseMapControlOpen && (this.state.doseMapSelected !== undefined || this.state.doseMapSelected !== "") &&
             this.state.doseMapType !== undefined)){
            allowToHideAlertPins = this.state.doseMapHideAlertPins;
        } else {
            allowToHideAlertPins = false;
        }

        let doseMapAnalysisInterface:DoseMapAnalysisInterface = {
            doseMapControlOpen: this.state.doseMapControlOpen,
            doseMapSelected: this.state.doseMapSelected,
            doseMapsAvailable: doseMapsAvailable,
            doseMapsAvailableStats: doseMapsAvailableStats,
            doseMapType: this.state.doseMapType,
            doseMapLive: this.state.doseMapLive,
            doseMapHideAlertPins: allowToHideAlertPins,
            doseMapAnalysisType: this.state.doseMapAnalysisType,
            doseMapBusy: this.state.doseMapBusy,
            doseMapScaleOverride:this.state.doseMapScaleOverride,
            doseMapDateSelected: this.state.doseMapDateSelected,
            doseMapDateAvailable: this.state.doseMapDateAvailable,

            selectDoseMap: (key: string | undefined) => {
                this.setState({
                    doseMapSelected: key,
                });
            },
            selectDoseMapType: (key: DoseMapTypesEnum | undefined) => {
                this.setState({
                    doseMapType: key,
                });
            },
            selectDoseMapDate: (key: string, date: string) => {
                this.setState(produce(draft => {
                    draft.doseMapDateSelected[key] = date;
                }));
            },
            selectDoseMapAnalysisType: (type: DoseAnalysisType|undefined) => {
                this.setState(produce(draft => {
                    draft.doseMapAnalysisType= type;
                }));
            },
            load: (key: string, message: DoseMapMessage) => {
                this.setState(produce(draft => {
                    //N.B. This is from file load process
                    let messages = [];
                    let messageA;
                    let messageB;
                    if(key==='A'){
                        messageA = message;
                        messageB = draft.doseMaps['B'];
                    } else if(key==='B'){
                        messageA = draft.doseMaps['A'];
                        messageB = message
                    }
                    messages.push(messageA);
                    messages.push(messageB);

                    const globalMinMax = DoseMapUtility.getGlobalDataMinMax(messages);
                    if(messageA){
                        messageA.globalScaleVal = globalMinMax;
                        messageA = DoseMapUtility.setDisplayMinMax(messageA,globalMinMax);
                        messageA = DoseMapUtility.updateDoseMaps(messageA);
                        draft.doseMaps['A'] = messageA;
                    }
                    if(messageB){
                        messageB.globalScaleVal = globalMinMax;
                        messageB = DoseMapUtility.setDisplayMinMax(messageB,globalMinMax);
                        messageB = DoseMapUtility.updateDoseMaps(messageB);
                        draft.doseMaps['B'] = messageB;
                    }
                    //this.setState(produce(draft => {
                    //draft.doseMaps[key!] = message;
                    draft.doseMapBusy[key!] = {flag:false,percent:-1};

                    draft.doseMapBusy['MAP'] = {flag:false,percent:-1};
                    //draft.doseMaps[key] = message;
                }));
            },
            selectLiveData: (flag: boolean) => {
                this.setState(produce(draft => {
                    draft.doseMapLive = !draft.doseMapLive;
                }));
            },
            setDoseMapBusy: (key: string, flag: boolean, percent?:number) => {
                //this.doseMapBusy[key] = flag;
                //console.log('set busy for '+key+' '+flag);
                this.setState(produce(draft => {

                    draft.doseMapBusy[key].flag = flag;
                    if(flag){
                        if(percent){
                            draft.doseMapBusy[key].percent = percent;
                        }
                    } else {
                        draft.doseMapBusy[key].percent = -1;
                    }
                }));
            },

            setScale: (key: string, type: DoseMapTypesEnum, minMax:DoseMapMinMaxEnum, value:number|null) => {

                    this.setState(produce(draft => {
                        if(key === 'C'){
                            if(minMax === DoseMapMinMaxEnum.MIN){
                                if(value === null){
                                    //N.B null value is a reset signal to data stat
                                    console.log('**setScale: reset signal!')
                                    value = draft.doseMaps[key].dataScaleVal[type].min
                                }
                                if(draft.doseMaps[key]){
                                    if(draft.doseMaps[key].dataScaleVal[type].min !== value){
                                        //if different from data scale, indicate override
                                        draft.doseMapScaleOverride[type][key].min = true;
                                        draft.doseMapScaleOverride[type][key].minVal = value;
                                    } else {
                                        draft.doseMapScaleOverride[type][key].min = false;
                                        draft.doseMapScaleOverride[type][key].minVal = undefined;
                                    }
                                    console.log('setting key: '+key+'value to '+value)
                                    draft.doseMaps[key].displayScaleVal[type].min = value;
                                }
                            }
                            if(minMax === DoseMapMinMaxEnum.MAX){
                                if(value === null){
                                    //N.B null value is a reset signal to data stat
                                    console.log('**setScale: reset signal!')
                                    value = draft.doseMaps[key].dataScaleVal[type].max
                                }
                                if(draft.doseMaps[key]){
                                    if(draft.doseMaps[key].dataScaleVal[type].max !== value){
                                        //if different from data scale, indicate override
                                        draft.doseMapScaleOverride[type][key].max = true;
                                        draft.doseMapScaleOverride[type][key].maxVal = value;
                                    } else {
                                        draft.doseMapScaleOverride[type][key].max = false;
                                        draft.doseMapScaleOverride[type][key].maxVal = undefined;
                                    }
                                    console.log('setting key: '+key+' value to '+value)
                                    draft.doseMaps[key].displayScaleVal[type].max = value;
                                }
                            }
                        } else {
                            // KEY IS A OR B
                            if(minMax === DoseMapMinMaxEnum.MIN){
                                if(value === null){
                                    //N.B null value is a reset signal to data stat
                                    console.log('**setScale: reset signal!')
                                    key = 'GANG'
                                    draft.doseMapScaleOverride[type]['GANG'].min = false;
                                }
                                if(key !== 'GANG'){
                                    //draft.doseMapScaleOverride[type][key].min = value;
                                    if(draft.doseMaps[key]){
                                        if(draft.doseMaps[key].globalScaleVal[type].min !== value){
                                            //if different from data scale, indicate override
                                            draft.doseMapScaleOverride[type][key].min = true;
                                            draft.doseMapScaleOverride[type][key].minVal = value;
                                        } else {
                                            draft.doseMapScaleOverride[type][key].min = false;
                                            draft.doseMapScaleOverride[type][key].minVal = undefined;
                                        }
                                        console.log('setting key: '+key+' value to '+value)
                                        draft.doseMaps[key].displayScaleVal[type].min = value;
                                    }
                                } else {
                                    //GANGED
                                    const gangkeys=['A','B'];
                                    for(const gangkey of gangkeys){
                                        if(draft.doseMaps[gangkey]){
                                            if(draft.doseMaps[gangkey]){
                                                let newval = value;
                                                if(newval === null){
                                                    //This is for reset
                                                    newval = draft.doseMaps[gangkey].globalScaleVal[type].min
                                                }
                                                if(draft.doseMaps[gangkey].globalScaleVal[type].min !== newval){
                                                    //if different from data scale, indicate override
                                                    draft.doseMapScaleOverride[type][gangkey].min = true;
                                                    draft.doseMapScaleOverride[type][gangkey].minVal = newval;
                                                    console.log('Turn on override')
                                                } else {
                                                    draft.doseMapScaleOverride[type][gangkey].min = false;
                                                    draft.doseMapScaleOverride[type][gangkey].minVal = undefined;
                                                    console.log('Turn off override')
                                                }
                                                console.log('setting key: '+gangkey+' value to '+newval)
                                                draft.doseMaps[gangkey].displayScaleVal[type].min = newval;
                                            }
                                        }
                                    }
                                }
                            }
                            if(minMax === DoseMapMinMaxEnum.MAX){
                                if(value === null){
                                    //N.B null value is a reset signal to data stat
                                    console.log('**setScale: reset signal!')
                                    key = 'GANG'
                                    draft.doseMapScaleOverride[type]['GANG'].max = false;
                                }
                                if(key !== 'GANG'){
                                    //draft.doseMapScaleOverride[type][key].min = value;
                                    if(draft.doseMaps[key]){
                                        if(draft.doseMaps[key].globalScaleVal[type].max !== value){
                                            //if different from data scale, indicate override
                                            draft.doseMapScaleOverride[type][key].max = true;
                                            draft.doseMapScaleOverride[type][key].maxVal = value;
                                        } else {
                                            draft.doseMapScaleOverride[type][key].max = false;
                                            draft.doseMapScaleOverride[type][key].maxVal = undefined;
                                        }
                                        console.log('setting key '+key+' value to '+value)
                                        draft.doseMaps[key].displayScaleVal[type].max = value;
                                    }
                                } else {
                                    //GANGED
                                    const gangkeys=['A','B'];
                                    for(const gangkey of gangkeys){
                                        if(draft.doseMaps[gangkey]){
                                            if(draft.doseMaps[gangkey]){
                                                let newval = value;
                                                if(newval === null){
                                                    //This is for reset
                                                    newval = draft.doseMaps[gangkey].globalScaleVal[type].max
                                                }
                                                if(draft.doseMaps[gangkey].globalScaleVal[type].max !== newval){
                                                    //if different from data scale, indicate override
                                                    draft.doseMapScaleOverride[type][gangkey].max = true;
                                                    draft.doseMapScaleOverride[type][gangkey].maxVal = newval;
                                                    console.log('Turn on override')
                                                } else {
                                                    draft.doseMapScaleOverride[type][gangkey].max = false;
                                                    draft.doseMapScaleOverride[type][gangkey].maxVal = undefined;
                                                    console.log('Turn off override')
                                                }
                                                console.log('setting key: '+gangkey+' value to '+newval)
                                                draft.doseMaps[gangkey].displayScaleVal[type].max = newval;
                                            }
                                        }
                                    }
                                }
                            } //MIN OR MAX
                        } //A OR B
                    }));
                //}
            },
            setOverride: (key: string, type: DoseMapTypesEnum, minMax:DoseMapMinMaxEnum, flag:boolean) => {
                this.setState(produce(draft => {
                    if(key === 'GANG'){
                        if(minMax === DoseMapMinMaxEnum.MIN){
                            draft.doseMapScaleOverride[type]['GANG'].min = flag;
                            //draft.doseMapScaleOverride[type]['B'].min = flag;
                        }
                        if(minMax === DoseMapMinMaxEnum.MAX){
                            draft.doseMapScaleOverride[type]['GANG'].max = flag;
                        }
                    } else {
                        if(minMax === DoseMapMinMaxEnum.MIN){
                            draft.doseMapScaleOverride[type][key].min = flag;
                        }
                        if(minMax === DoseMapMinMaxEnum.MAX){
                            draft.doseMapScaleOverride[type][key].max = flag;
                        }
                    }
                }));
            },
            setDoseMapHideAlertPins:(flag?:boolean) => {
                this.setState(produce(draft => {
                    if(flag!==undefined){
                        draft.doseMapHideAlertPins = flag;
                    } else {
                        draft.doseMapHideAlertPins = !draft.doseMapHideAlertPins;
                    }
                }));
            },
            setControlDisplayOpen:(flag?: boolean) => {
                this.setState(produce(draft => {
                    if(flag!==undefined){
                        draft.doseMapControlOpen = flag;
                    } else {
                        draft.doseMapControlOpen = !draft.doseMapControlOpen;
                    }
                }));
            },
        }

        let cloudUiDisplayInterface: CloudUiDisplayInterface= {
            device: this.state.cloudUiDisplayParam?.device,
            trackedDevice: this.state.trackedDevice,
            isShowing: this.state.cloudUiDisplayParam===undefined ? false : this.state.cloudUiDisplayParam.isShowing,
            url: this.state.cloudUiDisplayParam?.url,
            invUuid: this.state.cloudUiDisplayParam?.invUuid,
            close: ()=>{
                this.setState(produce(draft => {
                    draft.cloudUiDisplayParam = {device: '', url:''};
                    draft.selectedInvestigation = undefined;
                }));
            },
            open: (param:CloudUiDisplayParam)=>{
                this.setState(produce(draft => {
                    draft.cloudUiDisplayParam = {isShowing: true, device: param.device, url:param.url,invUuid:param.invUuid};
                }));
            },
            setTrackedDevice: (device:string|undefined)=>{
                this.setState(produce(draft => {
                    draft.trackedDevice = device;
                }));
            }
        }
        const suppressedAlertsInterface:SuppressedAlertsInterface = {
            showSuppressedAlerts: this.state.showSuppressedAlerts,
            setShowSuppressedAlerts: (flag:boolean)=>{
                this.setState({
                    showSuppressedAlerts: flag
                })
                if(flag===true){
                    uiStorage.setStoredUISetting('showSuppressedAlerts',flag);
                } else {
                    uiStorage.setStoredUISetting('showSuppressedAlerts',undefined);
                }
                //Reload UI automatically upon change
                document.location.reload();
            }
        }

        let timeSortedInvestigations = Object.values(this.state.investigations).sort(
            (lhs, rhs) => rhs.start_time - lhs.start_time);


        //Investigation filter
        if(this.state.invTypeToShow !== undefined && this.state.invTypeToShow !== null){
            timeSortedInvestigations = timeSortedInvestigations.filter((inv)=>{
                if('type' in inv){
                    return this.state.invTypeToShow?.includes(inv.type)
                } else {
                    //Default (Investigation message)
                    return true;
                }
            });
        }

        let mapLoadingIndicator: JSX.Element | undefined;
        if(this.state.doseMapBusy['MAP'] && this.state.doseMapBusy['MAP'].flag === true){
            mapLoadingIndicator =
                <div key={Date.now().toString()} style={{background:'#282828',position:'absolute',top:'40%',left:'50%',display:'flex',justifyContent:'center',zIndex:1000}}>
                    <div style={{ display: 'flex', position: 'absolute', top: '-100%', padding: '0px'}}>
                        <div className="contourloader"></div>
                    </div>
                    <div style={{ display: 'flex', position: 'absolute', top: '84px', padding: '0px',color:'gray'}}>
                        UPDATING...
                    </div>
                </div>
        } else if(this.state.replayInvestigation){
            const replay = this.state.investigations[this.state.replayInvestigation];

            if('type' in replay){
                if(replay.type === messages.Encounter.EncounterType.BIOSIGMA || replay.type === messages.Encounter.EncounterType.CHEMSIGMA){
                    const map = Object.keys(this.dataStream.getActiveQueryMap());
                    //console.log(map)
                    if(map.some(q=>q.includes('PSICBSigmaOutput'))){

                        mapLoadingIndicator =
                            <div style={{background:'#282828',position:'absolute',top:'40%',left:'50%',display:'flex',justifyContent:'center',zIndex:1000}}>
                                <div style={{ display: 'flex', position: 'absolute', top: '-100%', padding: '0px'}}>
                                    <div className="contourloader"></div>
                                </div>
                                <div style={{ display: 'flex', position: 'absolute', top: '84px', padding: '0px',color:'gray'}}>
                                    LOADING CONTOUR...
                                </div>
                            </div>
                    }
                }
            }
        }

        let investigationsCollapsed = Object.values(this.state.investigationsCollapsed).sort(
            (lhs, rhs) => lhs.start_time - rhs.start_time);

        let displays = [this.state.selectedDisplay1, this.state.selectedDisplay2].map((displayType: string, idx: number) => {
            if (displayType === 'Map') {

                let bioCountPlotDisplay;
                bioCountPlotDisplay =
                    <div style={{ position: "absolute", height: "58px", width:"63px",
                        zIndex:9998, right:'146px', bottom:'22px' }}>
                        <BioCountDisplayButton key={idx+2} devices={devices} selectedDeviceSensor={selectedDeviceSensor} plotSelectInterface={plotSelectInterface}
                            deviceInterface={deviceInterface} liveStream={liveStream} replayTime={replayTime} replayInvestigation={this.state.replayInvestigation}/>
                    </div>
                let trackThisSensorLocation;
                if(liveStream){
                    if(this.state.trackedDevice){
                        trackThisSensorLocation = this.state.trackedDevice;
                    }
                } else {
                    if(this.state.trackedDevice){
                        trackThisSensorLocation = this.state.trackedDevice;
                    }
                }
                /*
                if(liveStream){
                    if(this.state.selectedDeviceSensorRealtime?.follow){
                        trackThisSensorLocation = this.state.selectedDeviceSensorRealtime?.device
                    }
                } else {
                    if(this.state.selectedDeviceSensorReplay?.follow){
                        trackThisSensorLocation = this.state.selectedDeviceSensorReplay?.device
                    }
                }
                */
                //CBSigma output
                const cbSigmaOutputArray = this.getCBSigmaOutput();
                //**************************************************************
                //DOSE MAP ANALYSIS
                let thisDoseMap;
                if(this.state.doseMapSelected !== undefined){
                    thisDoseMap = this.state.doseMaps[this.state.doseMapSelected];
                }

                let doseMapAnalysisDisplay =
                    <div style={{ position: "absolute",
                        overflow: "inherit", zIndex:9990, right:'2px', top:'70px',flex:'1', opacity:0.90 }}>
                        <DoseMapAnalysisDisplay doseMaps={this.state.doseMaps} doseMapAnalysisInterface={doseMapAnalysisInterface}/>

                    </div>
                let doseMapControlClass = "";
                if(this.state.doseMapControlOpen){
                    doseMapControlClass = "active"
                }
                let doseMapToolButton;
                if(API_ROOT.Modalities.RadDoseMap){
                    doseMapToolButton=
                        <div style={{ position: "absolute", height: "60px", width:"63px",
                            zIndex:9998, right:'217px', bottom:'25px' }}>
                            <div className={"round-button "+doseMapControlClass} style={{margin: '5px', padding: '5px', height:'48px',width:'48px',fontSize:'0.7em'}}
                                onClick={
                                    (event)=>{
                                        doseMapAnalysisInterface.setControlDisplayOpen();
                                    }}
                            >
                                    <div>Dose Rate Map Tool</div>
                            </div>
                        </div>
                }
                //**************************************************************
                return (
                    <div style={{flex:1, position:'relative'}}>
                        {doseMapAnalysisDisplay}
                        <MultiSensorMapDisplay key={idx} investigations={timeSortedInvestigations}
                            selectedInvestigation={selectedInvestigation}
                            investigationInterface={investigationInterface}
                            liveStream={!this.state.replayInvestigation}
                            replayTime={replayTime}
                            sensorPositions={sensorPositions}
                            doseMap={thisDoseMap}
                            cbSigmaArray={cbSigmaOutputArray}
                            doseMapAnalysisInterface={doseMapAnalysisInterface}
                            trackThisSensorLocation={trackThisSensorLocation}/>
                        {doseMapToolButton}
                        {bioCountPlotDisplay}
                        {mapLoadingIndicator}
                    </div>
                )
            } else if (displayType === 'Count History') {
                //console.log('MultiSensorBioCountDisplay in app')
                return <MultiSensorBioCountDisplay key={idx} devices={devices} plotSelectInterface={plotSelectInterface}
                            healthData={this.state.health.nodes} liveStream={liveStream} replayTime={replayTime} replayInvestigation={this.state.replayInvestigation}/>

            } else if (displayType === 'Indoor Map') {
                let bioCountPlotDisplay;
                bioCountPlotDisplay =
                    <div style={{ position: "absolute", height: "58px", width:"63px",
                        zIndex:9998, right:'146px', bottom:'22px' }}>
                        <BioCountDisplayButton key={idx+2} devices={devices} selectedDeviceSensor={selectedDeviceSensor} plotSelectInterface={plotSelectInterface}
                            deviceInterface={deviceInterface} liveStream={liveStream} replayTime={replayTime} replayInvestigation={this.state.replayInvestigation}/>
                    </div>
                    let trackThisSensorLocation;
                    /*
                    if(liveStream){
                        if(this.state.selectedDeviceSensorRealtime?.follow){
                            trackThisSensorLocation = this.state.selectedDeviceSensorRealtime?.device
                        }
                    } else {
                        if(this.state.selectedDeviceSensorReplay?.follow){
                            trackThisSensorLocation = this.state.selectedDeviceSensorReplay?.device
                        }
                    }
                    */

                    //CBSigma output
                    const cbSigmaOutputArray = this.getCBSigmaOutput();
                return (
                    <>
                        <IndoorMapDisplay key={idx} investigations={timeSortedInvestigations}
                            selectedInvestigation={selectedInvestigation}
                            investigationInterface={investigationInterface}
                            sensorPositions={sensorPositions}
                            liveStream={!this.state.replayInvestigation}
                            //replayTime={this.state.replayTime? new Date(this.state.replayTime): undefined}
                            replayTime={replayTime}
                            cbSigmaArray={cbSigmaOutputArray}
                            trackThisSensorLocation={trackThisSensorLocation}/>
                        {bioCountPlotDisplay}
                        {mapLoadingIndicator}
                    </>
                )

            } else {
                let initLane;
                if (this.state.selectedDisplay1 === 'Context' && this.state.selectedDisplay2 === 'Context') {
                    initLane = idx;
                }

                //N.B. ContextDisplay will not be used in BD-21 UI so for initial commit case, this is commented out
                /*
                return <ContextDisplay key={idx} liveStream={liveStream}
                    investigations={Object.values(this.state.investigations)}
                    remoteDevice=""
                    multipleCamera={true}
                    initLane={initLane}
                    replayTime={replayTime}
                    images={this.state.investigationData?.images}
                    imageAnalyses={this.state.investigationData?.imageAnalyses}
                    track2d={this.state.investigationData?.track2d}
                    track3d={this.state.investigationData?.track3d}
                    attribution={this.state.investigationData?.attribution}
                    attributionAnalysis={this.state.investigationData?.attributionAnalysis} />
                    */
            }
        })

        let main =
            <div style={{ display: 'flex', flex: '1', flexDirection: 'row', overflow: 'hidden' }}>
                <div style={{ display: 'flex', flex: '1', flexDirection: 'column', marginRight: '2px', overflow: 'hidden' }}>
                    <div className="PaneSelector" style={{ flex: '0', display: 'flex', flexDirection: 'row' }}>
                        <select className="modern" defaultValue={this.state.selectedDisplay1}
                            onChange={(e) => this.setState({ selectedDisplay1: e.target.value })}>
                            {displayModes}
                        </select>
                    </div>
                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', marginTop: '2px', backgroundColor: '#101010', overflow: 'hidden', position: 'relative' }}>
                        {displays[0]}
                    </div>
                </div>
            </div>

        let invTypeToShow;
        if(this.getTier0Tier2Results){
            invTypeToShow = this.state.invTypeToShow;
        }
        const investigationDisplay =
        /*
        <BioInvestigationDisplay investigations={timeSortedInvestigations}
            selectedInvestigation={selectedInvestigation}
            replayInvestigation={replayInvestigation}
            investigationsCollapsed={investigationsCollapsed}
            investigationInterface={investigationInterface}
            showAudioControl={true} />
        */
        <BioInvestigationEncounterDisplay
            investigations={timeSortedInvestigations}
            selectedInvestigation={selectedInvestigation}
            currentPCSResult={null}
            currentNeutronResult={null}
            replayInvestigation={replayInvestigation}
            investigationsCollapsed={investigationsCollapsed}
            investigationInterface={investigationInterface}
            invTypeToShow={invTypeToShow}
            showAudioControl={true}
            useTTSVoice={this.useTTSVoice}
            showNodeLocationInsteadofSourceLocation={true}/>
            //showSourceLocationAsDirection={true}/>

        let timeSlider;
        let thisInvestigationPadding = this.investigationPadding;
        if(replayInvestigation && 'type' in replayInvestigation){
            if(replayInvestigation.type === messages.Encounter.EncounterType.BIOSIGMA || replayInvestigation.type === messages.Encounter.EncounterType.CHEMSIGMA){
                thisInvestigationPadding = 5*60000;
            }
        }
        timeSlider =
            <div id="time-slider-container" style={{ display: 'flex', flexDirection: 'row', zIndex: 3, paddingTop:'7px' }}
                className={this.state.replayInvestigation ? 'active' : 'inactive'} >
                <div style={{ flexGrow: 1 }}>
                    <input type="range"
                        min={(replayInvestigation?.start_time || 0) - thisInvestigationPadding}
                        max={(replayInvestigation?.end_time || 0) + thisInvestigationPadding}
                        step={200}
                        value={this.state.replayTime || 0}
                        onChange={(event) => this.setState({
                            replayTime:
                                this.normalizeReplayTime(+event.target.value)
                        })} />
                </div>
                <div style={{
                    display: "flex", flexDirection: 'row',
                    justifyContent: 'space-between', zIndex: 3, flexGrow: 0
                }}>
                    <div className="round-button"
                        onClick={() => {
                            this.setState((state) => {
                                return { replayTime: this.normalizeReplayTime((state?.replayTime || 0) - 200) }
                            })
                        }}>
                        <img src={previousFrame} alt="Previous Frame" style={{ width: '32px', height: '32px' }} />
                    </div>
                    <div className="round-button"
                        onClick={() => {
                            this.setState((state) => {
                                return { replayTime: this.normalizeReplayTime((state?.replayTime || 0) + 200) }
                            })
                        }}>
                        <img src={advanceFrame} alt="Next Frame" style={{ width: '32px', height: '32px' }} />
                    </div>
                </div>
            </div>

        let cloudUIOverlay;
        if(this.state.cloudUiDisplayParam?.isShowing){
            cloudUIOverlay = <CloudUiDisplay key={'cloudUiDisplay'} cloudUiDisplayInterface={cloudUiDisplayInterface}/>
        }

        return (
            <div className="App" style={{ display: 'flex', flexDirection: 'column' }}>
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
                {cloudUIOverlay}
                <BD21StatusHeader headerTitle='REACT' connected={this.state.connected} replayTime={replayTime}
                    modalityFilterInterface={modalityFilterInterface}
                    cloudUiDisplayInterface={cloudUiDisplayInterface}
                    suppressedAlertsInterface={suppressedAlertsInterface}
                    liveStream={liveStream} showTimeDisplay={true} healthData={this.state.health.nodes} uiVersionString={this.uiVersionString}/>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'row', height: "0px", overflow: 'hidden' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: '278px' }}>
                    {investigationDisplay}
                    </div>
                    <div className="MainDisplay" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {main}
                        </div>
                        {timeSlider}
                    </div>
                </div>
            </div>
        );
    }

}
App.contextType = AuthContext;

export default App;
declare global {
    interface Window {
        ConsoleUtility:any,
        app:any;
    }
}

window.ConsoleUtility = {
    showSuppressedAlertsMenu(){
        const suppressedAlertMenu = document.getElementById('suppressed-alerts-menu')
        if(suppressedAlertMenu){
            suppressedAlertMenu.classList.remove('hidden');
        }
    }
}
